import { SeeqNames } from '@seeqdev/constants';
import type { ActionBoardSourceColumn } from '@/actionBoards/addChart/actionBoardAddChart.types';
import type { AgentContextSearch } from '@/itemFinder/itemFinder.agentContext';
import type { Filter } from '@/serverSideTable/materializedTables/materializedTables.constants';
import type { SimplifiedNode } from '@/vantage/types/SimplifiedNode.types';
import type { MarkdownItemOutputV1 } from '@seeqdev/api/model/MarkdownItemOutputV1';
import type { TileOutputV1 } from '@seeqdev/api/model/TileOutputV1';
import type { WorksheetOutputV1 } from '@seeqdev/api/model/WorksheetOutputV1';

export type FontSize = 'small' | 'medium' | 'large';

export type FormulaAssistantVariant = 'debug' | 'explain';

export type AgentBuilderDisplayInfo = {
  id?: string;
  name: string;
  icon?: string;
  iconColor?: string;
};

export type ChatData = {
  messages: ChatMessage[];
  origin?: AssistantOrigin;
} & Record<string, any>;

export type ThinkingSectionStatus = 'running' | 'completed' | 'error' | 'unknown';

// New types for thinking sections
export type ThinkingSectionItemType = 'tool_call' | 'code_block' | 'context_retrieval' | 'reasoning_summary';

// Memory types
export interface Memory {
  id: string;
  content: string;
  timestamp: number;
  status: 'pending' | 'saved' | 'discarded';
  updateType?: 'new' | 'update'; // Track if this is a new memory or an update to existing
  previousContent?: string; // Store previous content for updates
}

// Memory event payload types for better type safety
export interface MemoryCreatedPayload {
  content: string;
  memory_id: string;
  is_user_request: boolean;
}

export interface MemoryUpdatedPayload {
  content: string;
  memory_id: string;
  previous_content?: string;
  is_user_request: boolean;
}
export interface ThinkingSectionItem {
  item_id: string; // Primary identifier for items - required
  type?: ThinkingSectionItemType; // Optional - can be empty for placeholders, filled in later
  name?: string; // Optional - can be empty for placeholders, filled in later
  description?: string; // Optional - can be empty for placeholders, filled in later
  content?: string; // For code blocks or detailed content
  status: ThinkingSectionStatus;
  startTime?: number;
  endTime?: number;
  duration?: number; // Pre-calculated duration in milliseconds
  metadata?: Record<string, any>; // For additional tool-specific data
}

export interface ThinkingSection {
  id: string;
  title: string;
  status: ThinkingSectionStatus;
  items: ThinkingSectionItem[];
  startTime: number;
  endTime?: number;
  duration?: number; // Pre-calculated duration in milliseconds
  isCollapsed?: boolean;
}

export type AttachmentType = 'image' | 'csv' | 'pdf';

export type ChatAttachment = {
  id: string;
  url: string;
  type: AttachmentType;
  mimeType: string;
  name?: string;
  size?: number;
  csvPreview?: string; // First ~20 rows of CSV content for preview purposes
};

// Tracks an in-flight upload. Image uploads show the image-style loading placeholder;
// document uploads (CSV/PDF) show a spinner pill with the file name in the document row.
export type UploadingFile = {
  id: string;
  isImage: boolean;
  name: string;
};

export type GuidedVisualizationSource = {
  id: string;
  name: string;
  type?: string;
  summary?: string | null; // undefined = not yet fetched, null = fetch failed, string = fetched
};

export const GUIDED_VISUALIZATION_TILE_TYPES = ['chart', 'kpi', 'table', 'filter'] as const;
export type GuidedVisualizationTileType = (typeof GUIDED_VISUALIZATION_TILE_TYPES)[number];

export const toGuidedActionBoardTranslationSuffix = (value: string) => value.replace(/-/g, '_').toUpperCase();

export const getGuidedActionBoardTileTypeLabelKey = (tileType: GuidedVisualizationTileType) =>
  `AI_ASSISTANT.GUIDED_ACTION_BOARD.TILE_TYPE.${toGuidedActionBoardTranslationSuffix(tileType)}.LABEL`;

export const getGuidedActionBoardTileTypeDescriptionKey = (tileType: GuidedVisualizationTileType) =>
  `AI_ASSISTANT.GUIDED_ACTION_BOARD.TILE_TYPE.${toGuidedActionBoardTranslationSuffix(tileType)}.DESCRIPTION`;

export type GuidedVisualizationColumnSelection = {
  id: string;
  name: string;
};

export const GUIDED_VISUALIZATION_CHART_TYPES = [
  'bar',
  'grouped-bar',
  'stacked-bar',
  'line',
  'area',
  'pie',
  'heatmap',
  'scatter',
  'bubble',
  'maps',
  'radial-gauge',
] as const;
export type GuidedVisualizationChartType = (typeof GUIDED_VISUALIZATION_CHART_TYPES)[number];

export const getGuidedActionBoardChartTypeLabelKey = (chartType: GuidedVisualizationChartType) =>
  `AI_ASSISTANT.GUIDED_ACTION_BOARD.CHART_TYPE.${toGuidedActionBoardTranslationSuffix(chartType)}.LABEL`;

export const getGuidedActionBoardChartTypeDescriptionKey = (chartType: GuidedVisualizationChartType) =>
  `AI_ASSISTANT.GUIDED_ACTION_BOARD.CHART_TYPE.${toGuidedActionBoardTranslationSuffix(chartType)}.DESCRIPTION`;

export type GuidedActionBoardMappingOption = {
  id: string;
  label: string;
  description: string;
  icon: string;
  promptHint: string;
};

export type GuidedVisualizationSuggestion = {
  id: string;
  chartType: GuidedVisualizationChartType;
  title: string;
  reason: string;
  promptHint: string;
};

export type GuidedSourceInsights = {
  summary: string;
  basis: 'journal' | 'columns';
  suggestedVisualizations: GuidedVisualizationSuggestion[];
};

export type GuidedVisualizationStep =
  | {
      step: 'source';
    }
  | {
      step: 'loading';
      title: string;
      description: string;
      source?: GuidedVisualizationSource;
    }
  | {
      step: 'source-insights';
      source: GuidedVisualizationSource;
      sourceColumns: ActionBoardSourceColumn[];
      sourceInsights: GuidedSourceInsights;
    }
  | {
      step: 'tile-type';
      source: GuidedVisualizationSource;
      sourceColumns: ActionBoardSourceColumn[];
      tileTypes: GuidedVisualizationTileType[];
    }
  | {
      step: 'chart-type';
      source: GuidedVisualizationSource;
      sourceColumns: ActionBoardSourceColumn[];
      tileType: 'chart';
      chartTypes: GuidedVisualizationChartType[];
    }
  | {
      step: 'column-suggestions';
      source: GuidedVisualizationSource;
      sourceColumns: ActionBoardSourceColumn[];
      tileType: 'chart';
      chartType: GuidedVisualizationChartType;
      suggestedColumnIds: string[];
    }
  | {
      step: 'column-mapping';
      source: GuidedVisualizationSource;
      tileType: 'chart';
      chartType: GuidedVisualizationChartType;
      columns: GuidedVisualizationColumnSelection[];
      mappingOptions: GuidedActionBoardMappingOption[];
    };

export type GuidedActionBoardStep =
  | GuidedVisualizationStep
  | {
      step: 'filter-column';
      boardId: string;
      calculatedTableId: string;
      columns: GuidedFlowColumn[];
      isLoadingSuggestions: boolean;
      suggestedColumns: GuidedFlowColumn[];
    }
  | {
      step: 'filter-created';
      columnNames: string[];
    };

export type GuidedActionBoardSelection =
  | {
      type: 'source';
      source: GuidedVisualizationSource;
    }
  | {
      type: 'source-insights';
      source: GuidedVisualizationSource;
      sourceColumns: ActionBoardSourceColumn[];
    }
  | {
      type: 'add-all-suggestions';
      source: GuidedVisualizationSource;
      sourceInsights: GuidedSourceInsights;
    }
  | {
      type: 'add-selected-suggestions';
      source: GuidedVisualizationSource;
      sourceInsights: GuidedSourceInsights;
      selectedSuggestions: GuidedVisualizationSuggestion[];
    }
  | {
      type: 'continue-manual';
      source: GuidedVisualizationSource;
      sourceColumns: ActionBoardSourceColumn[];
    }
  | {
      type: 'tile-type';
      tileType: GuidedVisualizationTileType;
      source: GuidedVisualizationSource;
      sourceColumns: ActionBoardSourceColumn[];
    }
  | {
      type: 'chart-type';
      chartType: GuidedVisualizationChartType;
      source: GuidedVisualizationSource;
      sourceColumns: ActionBoardSourceColumn[];
    }
  | {
      type: 'columns';
      chartType: GuidedVisualizationChartType;
      columns: GuidedVisualizationColumnSelection[];
      selectedSourceColumns: ActionBoardSourceColumn[];
      source: GuidedVisualizationSource;
    }
  | {
      type: 'mapping';
      chartType: GuidedVisualizationChartType;
      columns: GuidedVisualizationColumnSelection[];
      source: GuidedVisualizationSource;
      label: string;
      promptHint: string;
    }
  | {
      type: 'filter-columns';
      columns: GuidedFlowColumn[];
    };

export type GuidedFlowColumn = {
  id: string;
  name: string;
  type: string;
  source?: { id: string; name: string };
};

export type GuidedDrillDimensionLevelView = {
  id: string;
  name: string;
  description?: string;
  rank: number;
  columnName?: string;
};

export type GuidedDrillDimensionView = {
  id: string;
  name: string;
  description?: string;
  isGlobal: boolean;
  isEnabledOnBoard: boolean;
  levels: GuidedDrillDimensionLevelView[];
};

export type GuidedDrillDimensionSuggestionLevel = {
  name: string;
  columnId: string;
  rank: number;
};

export type GuidedDrillDimensionSuggestion = {
  id: string;
  name: string;
  description?: string;
  reason: string;
  levels: GuidedDrillDimensionSuggestionLevel[];
};

export type GuidedDrillDimensionLevelDraft = {
  draftId: string;
  existingLevelId?: string;
  name: string;
  description?: string;
  column?: GuidedFlowColumn;
  columnName?: string;
  nameTouched?: boolean;
};

export type GuidedDrillDimensionFormState = {
  dimensionId?: string;
  name: string;
  description?: string;
  isGlobal: boolean;
  wasGlobal?: boolean;
  wasEnabled?: boolean;
  levels: GuidedDrillDimensionLevelDraft[];
};

export type GuidedDrillDimensionStep =
  | { step: 'loading'; title: string; description: string }
  | { step: 'error'; title: string; description: string }
  | { step: 'success'; title: string; description: string }
  | {
      step: 'overview';
      kind: 'add' | 'manage';
      boardId: string;
      workbookId?: string;
      dimensions: GuidedDrillDimensionView[];
      calculatedTableColumns: GuidedFlowColumn[];
      suggestions: GuidedDrillDimensionSuggestion[];
      suggestionsRequested: boolean;
    }
  | ({
      step: 'editor';
      kind: 'add' | 'manage';
      boardId: string;
      workbookId?: string;
      formState: GuidedDrillDimensionFormState;
      calculatedTableColumns: GuidedFlowColumn[];
    } & (
      | { mode: 'create'; creationPath: GuidedDrillDimensionCreationPath }
      | { mode: 'edit' | 'enable'; creationPath?: never }
    ));

export type GuidedDrillDimensionSaveOutcome = { kind: 'success'; enabledColumns: string[] } | { kind: 'error' };

export type GuidedDrillDimensionManageOutcome = { kind: 'success' } | { kind: 'error' };

export type GuidedDrillDimensionCreationPath = 'Manual' | 'Suggested';

export type GuidedDrillDimensionSelection =
  | { type: 'back-to-overview'; kind: 'add' | 'manage'; boardId: string }
  | { type: 'load-suggestions'; boardId: string }
  | {
      type: 'manual-create';
      boardId: string;
      workbookId?: string;
      calculatedTableColumns: GuidedFlowColumn[];
    }
  | {
      type: 'apply-suggestion';
      boardId: string;
      workbookId?: string;
      suggestion: GuidedDrillDimensionSuggestion;
      calculatedTableColumns: GuidedFlowColumn[];
    }
  | {
      type: 'edit-existing';
      boardId: string;
      workbookId?: string;
      dimension: GuidedDrillDimensionView;
      calculatedTableColumns: GuidedFlowColumn[];
      editorMode?: 'edit' | 'enable';
    }
  | {
      type: 'save';
      kind: 'add' | 'manage';
      boardId: string;
      workbookId?: string;
      formState: GuidedDrillDimensionFormState;
      outcome: GuidedDrillDimensionSaveOutcome;
    }
  | {
      type: 'delete-existing';
      kind: 'add' | 'manage';
      boardId: string;
      dimensionId: string;
      dimensionName: string;
      outcome: GuidedDrillDimensionManageOutcome;
    }
  | {
      type: 'disable-existing';
      boardId: string;
      dimensionId: string;
      dimensionName: string;
      outcome: GuidedDrillDimensionManageOutcome;
    };

export type ParsedActionBoardChoice = {
  key?: string;
  label: string;
  prompt: string;
  translationKey?: never;
  ui?: 'default';
};

export type GuidedActionBoardDirectEditorChoice = {
  key: string;
  label?: never;
  prompt?: never;
  translationKey: string;
  ui:
    | 'color_picker'
    | 'chart_color_editor'
    | 'gauge_solid_color_editor'
    | 'gauge_band_editor'
    | 'tile_background_editor'
    | 'kpi_precision_editor'
    | 'kpi_threshold_editor'
    | 'kpi_unit_editor'
    | 'axis_label_editor'
    | 'series_rename_editor'
    | 'series_delete_editor'
    | 'series_label_display_editor'
    | 'map_tooltip_label_editor'
    | 'table_column_editor'
    | 'title_subtitle_editor'
    | 'table_title_visibility_editor'
    | 'value_format_editor'
    | 'chart_type_editor';
};

export type ActionBoardChoice = ParsedActionBoardChoice | GuidedActionBoardDirectEditorChoice;

export type ActionBoardChoiceColorTarget = {
  targetLabel: string;
  currentColor?: string | null;
};

export type GuidedActionBoardEditTile = Pick<
  TileOutputV1,
  | 'aggregateColumns'
  | 'backgroundColor'
  | 'chartOptions'
  | 'conditionalFormatting'
  | 'decimalPlaces'
  | 'id'
  | 'groupBy'
  | 'kpiData'
  | 'name'
  | 'rehydrationInstructions'
  | 'tileConfiguration'
  | 'type'
  | 'unit'
  | 'valueColumn'
>;

export type ExternalPromptPayload = {
  kind?: 'prompt';
  prompt: string;
  agent?: AgentType;
  insertOnly?: boolean;
  origin?: AssistantOrigin;
  displayPrompt?: string;
  // Captured at click time by a Data Lab cell action.
  dataLabContext?: DataLabContext;
};

export type ExternalGuidedActionBoardEditPayload = {
  kind: 'guidedActionBoardEdit';
  displayPrompt?: string;
  guidedEditTileId: string;
};

export type ExternalGuidedDrillDimensionPayload = {
  kind: 'guidedDrillDimension';
  boardId: string;
  mode: 'add' | 'manage';
};

export type ExternalGuidedFilterPayload = {
  kind: 'guidedFilter';
  boardId: string;
};

export type ExternalAIAssistantPayload =
  | ExternalPromptPayload
  | ExternalGuidedActionBoardEditPayload
  | ExternalGuidedDrillDimensionPayload
  | ExternalGuidedFilterPayload;

export type ChatSubmitOptions = {
  actionBoardChoicesOnCompletion?: ActionBoardChoice[];
  displayPrompt?: string;
  selectedDocument?: SelectedDocument | SelectedDocument[] | null;
  // Retry/edit set this so a resubmit reuses the original message's effort instead of the
  // current selector value.
  reasoningEffort?: ReasoningEffort;
  // Pre-gathered Data Lab context; when absent, a Data Lab `code` prompt asks the extension for it.
  dataLabContext?: DataLabContext;
};

export const STAGE_KNOWLEDGE_SOURCES = 'knowledge_sources' as const;

export type TokenUsage = {
  inputTokens: number;
  outputTokens: number;
  contextWindow?: number;
  maxOutputTokens?: number;
};

export type ChatMessage = {
  role: 'bot' | 'user';
  dialog: string;
  actionBoardChoices?: ActionBoardChoice[];
  submittedPrompt?: string;
  guidedEditTileId?: string;
  guidedEditEntry?: boolean;
  id: string;
  chatId?: string;
  agentType?: string;
  workflowId?: string;
  workflowName?: string;
  last?: boolean;
  agent?: string;
  origin?: AssistantOrigin;
  originDetails?: AssistantOriginDetails;
  stages?: Record<string, string | string[] | object>;
  components?: string[];
  prompt_classification?: string;
  selectedDocument?: SelectedDocument | SelectedDocument[];
  reasoningEffort?: ReasoningEffort; // Effort this message was sent with (reasoning-lane agents)
  thinkingSections?: ThinkingSection[]; // New field for thinking sections
  memories?: Memory[]; // New field for memories
  attachments?: ChatAttachment[]; // New field for image attachments
  imageUrl?: string;
  imageAlt?: string;
  imageName?: string;
  attachmentId?: string;
  attachmentUrl?: string;
  versionLabel?: string;
  guidedActionBoardStep?: GuidedActionBoardStep;
  guidedDrillDimensionStep?: GuidedDrillDimensionStep;
  tokenUsage?: TokenUsage;
};

export const CONTEXT_BUTTON_AGENTS = ['aq']; // Agents we show the context button for

export type AgentType = 'formula' | 'general' | 'code' | 'actions' | 'aq' | 'tome';
export type ReasoningStyle = 'enabled' | 'disabled';

// User-selectable reasoning effort, ordered from least to most effort.
export type ReasoningEffort = 'none' | 'low' | 'medium' | 'high';
export const REASONING_EFFORT_ORDER: ReasoningEffort[] = ['none', 'low', 'medium', 'high'];
export const DEFAULT_REASONING_EFFORT: ReasoningEffort = 'low';
// Reasoning-lane agents that honor the reasoning effort selector (Agent Q + the Q&A agents).
export const REASONING_EFFORT_AGENTS: AgentType[] = ['aq', 'general', 'formula', 'code'];

export function getDefaultReasoningEffort(agent: AgentType | undefined): ReasoningEffort {
  return agent === 'aq' ? 'medium' : DEFAULT_REASONING_EFFORT;
}

export const assistantLocations = {
  homescreen: SeeqNames.Origins.HomeScreen,
  workbench: SeeqNames.Origins.Workbench,
  organizer: SeeqNames.Origins.Organizer,
  vantage: SeeqNames.Consumption.Vantage,
  datalab: SeeqNames.Origins.DataLab,
  admin: SeeqNames.Origins.Administration,
  action_board: SeeqNames.Origins.ActionBoard,
  skillsbuilder: 'skillsbuilder',
  tree_builder: 'tree_builder',
} as const;

export type AssistantOrigin = keyof typeof assistantLocations | typeof SeeqNames.Origins.LogTracker;
export type AssistantOriginDetails = { label: string; url: string };

/**
 * DO NOT ACCESS THIS DIRECTLY. Use `genAIAgents()`  or getDataLabAiAgents() instead.
 */
export const agents: Agent[] = [
  {
    name: 'General',
    color: '#39516b',
    darkColor: '#54749a',
    key: 'general',
    icon: 'fc-genai-chat',
    description: 'AI_ASSISTANT.AGENT_INTRO_GENERAL',
  },
  {
    name: 'Formula',
    color: '#427c63',
    key: 'formula',
    icon: 'fc-formula-ai',
    description: 'AI_ASSISTANT.AGENT_INTRO_FORMULA',
  },
  {
    name: 'Data Lab',
    color: '#f26722',
    key: 'code',
    icon: 'fc-datalab-ai',
    description: 'AI_ASSISTANT.AGENT_INTRO_DATA_LAB',
  },
  {
    name: 'Actions',
    color: '#0491c2',
    key: 'actions',
    icon: 'fc-workbook-ai',
    description: 'AI_ASSISTANT.AGENT_INTRO_ACTIONS',
  },
  {
    name: 'Agent Q',
    color: '#ff2249',
    key: 'aq',
    icon: 'fc-workbook-ai',
    description: 'AI_ASSISTANT.AGENT_INTRO_VANTAGE',
  },
  {
    name: 'Document',
    color: '#4f596b',
    key: 'tome',
    icon: 'fa-book',
    description: 'AI_ASSISTANT.AGENT_INTRO_TOME',
    commandName: 'tome',
  },
];

export interface Agent {
  name: string;
  color: string;
  darkColor?: string;
  key: AgentType;
  icon: string;
  description: string;
  commandName?: string;
}

// To ADD feature flag for this
export const getDataLabAiAgents = () => {
  return 'general,formula,code';
};

export interface HistoryEntryType {
  chatId: string;
  origin?: AssistantOrigin;
  name: string;
  sharedBy?: string;
  shared: boolean;
  updatedAt?: string;
  favorite: boolean;
}

export interface GroupedHistoryEntryType {
  favorites: HistoryEntryType[];
  shared_with_me: HistoryEntryType[];
  today: HistoryEntryType[];
  yesterday: HistoryEntryType[];
  this_week: HistoryEntryType[];
  last_week: HistoryEntryType[];
  this_month: HistoryEntryType[];
  last_month: HistoryEntryType[];
  older: HistoryEntryType[];
}

export enum MagiqPromptType {
  IMPACT_REPORT_WORKBENCH = 'impact_report_workbench',
  IMPACT_REPORT_ORGANIZER = 'impact_report_organizer',
  IMPACT_REPORT_VANTAGE = 'impact_report_vantage',
}

export enum PromptCategory {
  FindAndCleanseData = 'Find & Cleanse Data',
  EventsAndPeriodsOfInterest = 'Events & Periods of Interest',
  AnalyzeAndQuantify = 'Analyze & Quantify',
  VisualizeAndGraph = 'Visualize & Graph',
  ModelAndPredict = 'Model & Predict',
  ScaleAnalytics = 'Scale Analytics',
  DocumentAndShare = 'Document & Share',
  ScriptAndCode = 'Script & Code',
  AdminAndDataManagement = 'Admin & Data Management',
  ReferenceYourDocs = 'Reference Your Docs',
  SeeqUseCase = 'AI_ASSISTANT.PROMPT_LIBRARY.CATEGORIES.SEEQ_USE_CASE',
  MyUseCase = 'AI_ASSISTANT.PROMPT_LIBRARY.CATEGORIES.MY_USE_CASE',
  SharedUseCase = 'AI_ASSISTANT.PROMPT_LIBRARY.CATEGORIES.SHARED_WITH_ME',
}

export enum LibraryPurpose {
  UseCase = 'Use Case',
  Prompt = 'Prompt',
}

export interface PromptEntry {
  title: string;
  category: PromptCategory;
  prompt: string;
  agentType: AgentType;
}

// Extended interface based on MarkdownItemOutputV1 with additional prompt-specific fields
export interface LibraryItem extends MarkdownItemOutputV1 {
  agentType: AgentType;
  category: PromptCategory;
  prompt: string;
  purpose: LibraryPurpose;
}

export interface DocumentItem {
  id: string;
  type: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  isPinned: boolean;
  owner: {
    id: string;
    name: string;
    type: string;
    username: string;
    email: string;
  };
  effectivePermissions: {
    read: boolean;
    write: boolean;
    manage: boolean;
  };
  isArchived: boolean;
}

export interface DocumentsResponse {
  content: DocumentItem[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

export interface WorksheetsResponse {
  worksheets: WorksheetOutputV1[];
  offset: number;
  limit: number;
}

export interface DocumentWithWorksheets extends DocumentItem {
  worksheets?: WorksheetOutputV1[];
}

/**
 * Replicated in genai/app/utils/types.py
 * Do not make changes without also porting them to that file
 */
export interface SelectedDocument {
  id: string;
  name: string;
  type: string;
  title?: string;
  contentType?: string;
  workbookId?: string;
  projectId?: string;
  folderId?: string;
  seeqUrl?: string;
  worksheetId?: string;
  worksheetName?: string;
  parentId?: string; // For worksheet's parent document
}

export type ContextMessageProps = {
  selectedDocument?: SelectedDocument | SelectedDocument[] | null;
};

const CONTEXT_MESSAGE_DOCUMENT_ICON_BY_TYPE: Partial<Record<string, string>> = {
  Analysis: 'fc fc-analysis',
  Topic: 'fc fc-report',
  Report: 'fc fc-report',
  Project: 'fc fc-seeq-datalab',
  Folder: 'fa-folder-open',
  Vantage: 'fc-vantage',
};

const CONTEXT_MESSAGE_DOCUMENT_ICON_CLASS_BY_TYPE: Partial<Record<string, string>> = {
  Topic: 'workbenchDisplay workbenchTopic',
  Project: 'workbenchDisplay workbenchProject',
  Folder: 'workbenchDisplay sq-text-primary',
  Vantage: 'workbenchDisplay workbenchVantage',
};

export const getContextMessageDocumentIcon = (type: string): string =>
  CONTEXT_MESSAGE_DOCUMENT_ICON_BY_TYPE[type] ?? 'fa-file-alt';

export const getContextMessageDocumentIconClass = (type: string): string =>
  CONTEXT_MESSAGE_DOCUMENT_ICON_CLASS_BY_TYPE[type] ?? 'workbenchDisplay';

export interface AIMemory {
  id: string;
  content: string;
  created_at: string;
  updated_at: string;
}

export interface MemoriesResponse {
  approved: AIMemory[];
  pending: AIMemory[];
}

// Document type priority for sorting
export const DOCUMENT_TYPE_PRIORITY: Record<string, number> = {
  Topic: 1,
  Vantage: 2,
  Analysis: 3,
};

/**
 * Supported image types for upload
 */
export const SUPPORTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];

/**
 * Supported CSV type for upload
 */
export const SUPPORTED_CSV_TYPE = 'text/csv';

/**
 * Supported PDF type for upload
 */
export const SUPPORTED_PDF_TYPE = 'application/pdf';

/**
 * Maximum file size for image uploads (10MB)
 */
export const MAX_IMAGE_FILE_SIZE = 10 * 1024 * 1024;

/**
 * Maximum file size for CSV uploads (10MB)
 */
export const MAX_CSV_FILE_SIZE = 10 * 1024 * 1024;

/**
 * Maximum file size for PDF uploads (25MB)
 */
export const MAX_PDF_FILE_SIZE = 25 * 1024 * 1024;

/**
 * Maximum width for image uploads (1920px)
 */
export const MAX_WIDTH = 1920;
/**
 * Maximum height for image uploads (1080px)
 */
export const MAX_HEIGHT = 1080;
/**
 * Scale step for image uploads (0.85)
 */
export const SCALE_STEP = 0.85;

/**
 * Maximum number of images allowed per message
 */
export const MAX_ATTACHMENTS_PER_MESSAGE = 5;

/**
 * Response interface for file upload (images and CSV)
 */
export interface FileUploadResponse {
  id: string;
  url: string;
}

/**
 * Replicated in genai/app/utils/types.py
 * Do not make changes without also porting them to that file
 */
export interface DataLabContext {
  activeFileName?: string;
  activeFileType?: string;
  activeFileContent?: string | Record<string, any>;
  activeCellContent?: string;
  // Sent separately because the backend strips outputs from an oversized notebook.
  activeCellOutputs?: string;
}

/**
 * Replicated in genai/app/utils/types.py
 * Do not make changes without also porting them to that file
 */
export interface VantageContext {
  timeRange: string;
  gridState: any;
  tableFilter?: Filter;
  virtualColumns?: string[];
  groupColumns?: string[];
  selectionFilter?: Filter;
  /** Legacy CSRM wire shape; backend prefers `selectionFilter` when both are sent. */
  selection?: SimplifiedNode[];
}

/**
 * Replicated in genai/app/utils/types.py
 * Do not make changes without also porting them to that file
 */
export interface WorkstepContext {
  id: string;
  createdAt: number;
  version: number;
  state: any;
}

/**
 * Replicated in genai/app/utils/types.py
 * Do not make changes without also porting them to that file
 */
export interface ChatContext {
  workstep?: WorkstepContext;
  selectedDocument?: SelectedDocument | SelectedDocument[];
  dataLab?: DataLabContext;
  timezone?: string;
  vantage?: VantageContext;
  actionBoardId?: string;
  treeBuilderTableId?: string;
  /** Current Item Finder search form state, sent only while a search-driven table editor is open. */
  itemFinderSearches?: AgentContextSearch[];
}

export interface MigrateUserChatsResponse {
  message: string;
  migrated: number;
  failed: number;
  orphans: number;
  orphans_removed: number;
}

/**
 * ask_user (clarifying question popup) types.
 * Wire shape mirrors genai/app/agents/common/tools/ask_user_types.py.
 */
export type AskUserAnswerKind = 'choice' | 'other' | 'skipped';

export interface AskUserOption {
  letter: string;
  label: string;
  /**
   * Optional complete i18n key for `label`, supplied only for statically authored (Python-side)
   * questions. `label` stays the English fallback and remains the value echoed back on submission.
   */
  labelKey?: string;
}

export interface AskUserAnswer {
  kind: AskUserAnswerKind;
  value: string;
  letter?: string;
}

export type AskUserQuestionStatus = 'pending' | 'answered' | 'skipped';

export interface AskUserQuestion {
  id: string;
  chatId: string;
  messageId: string;
  prompt: string;
  /**
   * Optional complete i18n key for `prompt`, supplied only for statically authored (Python-side)
   * questions. `prompt` stays the English fallback.
   */
  promptKey?: string;
  options: AskUserOption[];
  allowOther: boolean;
  status: AskUserQuestionStatus;
  answer?: AskUserAnswer;
  createdAt: number;
}

export interface AskUserDraft {
  letter?: string;
  otherText?: string;
}

/**
 * Wire shape of one option inside `question_popup_start`. Distinct from the store's
 * `AskUserOption` because the wire is snake_case; `toAskUserOptions` maps between them.
 */
export interface QuestionPopupStartOption {
  letter: string;
  label: string;
  /** Optional complete i18n key for `label`; absent for model-generated questions. */
  label_key?: string;
}

export interface QuestionPopupStartEvent {
  type: 'question_popup_start';
  question_id: string;
  chat_id: string;
  message_id: string;
  prompt: string;
  /** Optional complete i18n key for `prompt`; absent for model-generated questions. */
  prompt_key?: string;
  options: QuestionPopupStartOption[];
  allow_other: boolean;
}

export interface QuestionPopupResolvedEvent {
  type: 'question_popup_resolved';
  question_id: string;
  chat_id: string;
  answer: AskUserAnswer;
}

export interface QuestionPopupTimeoutEvent {
  type: 'question_popup_timeout';
  question_id: string;
  chat_id: string;
}

export type QuestionPopupEvent = QuestionPopupStartEvent | QuestionPopupResolvedEvent | QuestionPopupTimeoutEvent;

export interface AnswerSubmissionPayload {
  question_id: string;
  kind: AskUserAnswerKind;
  value: string;
  letter?: string;
}

export interface AnswerSubmissionResponse {
  resolved: string[];
  unknown: string[];
  already_resolved: string[];
}
